import type { PaddleConfigData } from '../types';

// Paddle.js v2, loaded once and initialised once. Events fan out to whoever listens.
// https://developer.paddle.com/paddlejs/overview

export type PaddleEvent = { name?: string; error?: unknown; data?: unknown };

type CheckoutSettings = {
  displayMode: 'inline' | 'overlay';
  frameTarget?: string;
  frameInitialHeight?: number;
  frameStyle?: string;
  theme: 'dark' | 'light';
  allowLogout: boolean;
  variant?: 'one-page' | 'multi-page';
};

export type PaddleSdk = {
  Environment: { set: (environment: 'sandbox' | 'production') => void };
  Initialize: (options: { token: string; eventCallback: (event: PaddleEvent) => void }) => void;
  Checkout: {
    open: (options: { transactionId: string; settings: CheckoutSettings }) => void;
    close: () => void;
  };
};

declare global {
  interface Window {
    Paddle?: PaddleSdk;
  }
}

const PADDLE_SRC = 'https://cdn.paddle.com/paddle/v2/paddle.js';
const listeners = new Set<(event: PaddleEvent) => void>();
let scriptPromise: Promise<PaddleSdk> | null = null;
let initializedToken: string | null = null;

function loadScript(): Promise<PaddleSdk> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  scriptPromise ??= new Promise<PaddleSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PADDLE_SRC;
    script.async = true;
    script.onload = () =>
      window.Paddle ? resolve(window.Paddle) : reject(new Error('Paddle.js did not start'));
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Paddle.js did not load'));
    };
    document.head.append(script);
  });
  return scriptPromise;
}

/** The ready Paddle SDK for this workspace's environment and client token. */
export async function getPaddle(config: PaddleConfigData): Promise<PaddleSdk> {
  const paddle = await loadScript();
  if (initializedToken !== config.clientToken) {
    if (config.environment === 'SANDBOX') paddle.Environment.set('sandbox');
    paddle.Initialize({
      token: config.clientToken,
      eventCallback: (event) => {
        for (const listener of listeners) listener(event);
      },
    });
    initializedToken = config.clientToken;
  }
  return paddle;
}

export function onPaddleEvent(listener: (event: PaddleEvent) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
